import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from 'src/app/shared/user.service';

@Component({
  selector: 'app-error-fallback',
  templateUrl: './error-fallback.component.html',
  styleUrls: ['./error-fallback.component.scss']
})
export class ErrorFallbackComponent implements OnInit {
  errorType: string = 'unknown';
  errorMessage: string = 'An unexpected error occurred';
  errorDetails: string = '';
  showRetry: boolean = true;
  showBackToLogin: boolean = true;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    // Check for error parameters in query string
    this.route.queryParams.subscribe(params => {
      this.errorType = params['type'] || 'unknown';
      this.errorMessage = params['message'] || this.getDefaultMessage(this.errorType);
      this.errorDetails = params['details'] || '';

      // Check URL for error parameters (in case they come from backend redirect)
      const urlParams = new URLSearchParams(window.location.search);
      const urlError = urlParams.get('error');
      const urlErrorDescription = urlParams.get('error_description');
      
      if (urlError && !this.errorDetails) {
        this.errorType = 'oauth2_callback';
        this.errorMessage = this.getErrorMessageForCode(urlError, urlErrorDescription);
        this.errorDetails = `Error code: ${urlError}`;
      }

      // Check if this is an OAuth2 callback error
      if (window.location.pathname.includes('/login/oauth2/code/') || 
          window.location.pathname.includes('/api1/login/oauth2/code/') ||
          window.location.pathname.includes('/api1/login')) {
        this.errorType = 'oauth2_callback';
        if (!this.errorMessage || this.errorMessage === 'An unexpected error occurred') {
          this.errorMessage = 'OAuth2 authentication failed';
          this.errorDetails = 'The authentication callback could not be processed. Please try logging in again.';
        }
      }
    });

    // Also check URL path for OAuth2 errors
    const currentPath = window.location.pathname;
    if (currentPath.includes('oauth2') || (currentPath.includes('error') && !currentPath.includes('/error'))) {
      this.errorType = 'oauth2_callback';
    }
  }

  getErrorMessageForCode(errorCode: string, errorDescription?: string): string {
    const errorMessages: { [key: string]: string } = {
      'access_denied': 'Access denied. You do not have permission to access this application.',
      'invalid_request': 'Invalid authentication request. Please try again.',
      'unauthorized_client': 'This application is not authorized to perform this action.',
      'unsupported_response_type': 'The authentication server does not support this response type.',
      'invalid_scope': 'The requested scope is invalid or not supported.',
      'server_error': 'An error occurred on the authentication server. Please try again later.',
      'temporarily_unavailable': 'The authentication service is temporarily unavailable. Please try again later.'
    };

    return errorDescription || errorMessages[errorCode] || 'OAuth2 authentication failed. Please try again.';
  }

  getDefaultMessage(type: string): string {
    const messages: { [key: string]: string } = {
      'oauth2_callback': 'OAuth2 authentication failed. The callback could not be processed.',
      'token_validation': 'Token validation failed. Please try logging in again.',
      'network': 'Network error. Please check your internet connection and try again.',
      'unauthorized': 'You are not authorized to access this resource.',
      'not_found': 'The requested resource was not found.',
      'server_error': 'A server error occurred. Please try again later.',
      'unknown': 'An unexpected error occurred. Please try again.'
    };
    return messages[type] || messages['unknown'];
  }

  getErrorIcon(): string {
    const icons: { [key: string]: string } = {
      'oauth2_callback': 'heroXCircle',
      'token_validation': 'heroShieldExclamation',
      'network': 'heroWifi',
      'unauthorized': 'heroLockClosed',
      'not_found': 'heroMagnifyingGlass',
      'server_error': 'heroExclamationTriangle',
      'unknown': 'heroExclamationCircle'
    };
    return icons[this.errorType] || icons['unknown'];
  }

  retryLogin(): void {
    this.userService.initiateSSOLogin();
  }

  goToLogin(): void {
    this.router.navigate(['/login']);
  }

  goToDashboard(): void {
    const currentUser = localStorage.getItem('currentUser');
    if (currentUser && currentUser !== 'null') {
      this.router.navigate(['/app/dashboard']);
    } else {
      this.goToLogin();
    }
  }
}

